import { useState, useEffect, useCallback, useRef } from 'react'
import { requestPermissionAndGetToken, onForegroundMessage } from '../lib/fcm'

export interface AppNotification {
  id: string
  title: string
  body: string
  deep_link: string
  reference_kind: string
  reference_id: string
  created_at: string
  read: boolean
}

interface NotificationsResponse {
  notifications: AppNotification[]
  unread_count: number
}

interface SSEPayload {
  type: string
  id: string
  title: string
  body: string
  deep_link: string
  reference_kind: string
  reference_id: string
  created_at: string
}

function playNotificationChime(): void {
  try {
    const ctx = new AudioContext()
    const gain = ctx.createGain()
    gain.connect(ctx.destination)
    gain.gain.setValueAtTime(0.3, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6)

    ;[880, 1100].forEach((freq, i) => {
      const osc = ctx.createOscillator()
      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.12)
      osc.connect(gain)
      osc.start(ctx.currentTime + i * 0.12)
      osc.stop(ctx.currentTime + i * 0.12 + 0.25)
    })
  } catch {
    // AudioContext blocked (no user gesture yet) — silently skip
  }
}

function nextRetryDelay(attempt: number): number {
  return Math.min(2000 * 2 ** attempt, 30_000)
}

const MAX_NOTIFICATIONS = 50

export interface UseNotificationsResult {
  notifications: AppNotification[]
  unreadCount: number
  markRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  clearAll: () => Promise<void>
  dismissOne: (id: string) => Promise<void>
  loading: boolean
}

export function useNotifications(onNewNotification?: (n: AppNotification) => void): UseNotificationsResult {
  const [dbNotifications, setDbNotifications] = useState<AppNotification[]>([])
  const [sseNotifications, setSseNotifications] = useState<AppNotification[]>([])
  const [dbUnreadCount, setDbUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const esRef = useRef<EventSource | null>(null)
  const retryAttempt = useRef(0)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const unmounted = useRef(false)
  const connectRef = useRef<() => void>(() => {})

  // Merge SSE-pushed (newest, deduplicated) on top of DB results
  const sseIds = new Set(sseNotifications.map(n => n.id))
  const notifications = [
    ...sseNotifications,
    ...dbNotifications.filter(n => !sseIds.has(n.id)),
  ].slice(0, MAX_NOTIFICATIONS)

  const sseUnreadCount = sseNotifications.filter(n => !n.read).length
  const unreadCount = dbUnreadCount + sseUnreadCount

  const fetchStored = useCallback(async () => {
    try {
      const resp = await fetch('/api/v1/notifications', { credentials: 'include' })
      if (!resp.ok) return
      const data = await resp.json() as NotificationsResponse
      setDbUnreadCount(data.unread_count ?? 0)
      setDbNotifications(data.notifications ?? [])
    } catch {
      // non-fatal
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchStored()
  }, [fetchStored])

  // SSE subscription — proxied through elbooks backend → elmessagehub gRPC stream
  connectRef.current = () => {
    if (unmounted.current) return
    const es = new EventSource('/api/v1/notifications/sse', { withCredentials: true })
    esRef.current = es

    es.onopen = () => {
      retryAttempt.current = 0
      void fetchStored()
    }

    es.onmessage = (evt) => {
      try {
        const payload = JSON.parse(evt.data as string) as SSEPayload
        if (payload.type !== 'notification') return

        const n: AppNotification = {
          id: payload.id,
          title: payload.title,
          body: payload.body,
          deep_link: payload.deep_link,
          reference_kind: payload.reference_kind,
          reference_id: payload.reference_id,
          created_at: payload.created_at,
          read: false,
        }

        setSseNotifications(prev => {
          const deduped = prev.filter(x => x.id !== n.id)
          return [n, ...deduped].slice(0, MAX_NOTIFICATIONS)
        })
        playNotificationChime()
        onNewNotification?.(n)
      } catch {
        // ignore malformed events
      }
    }

    es.onerror = () => {
      es.close()
      esRef.current = null
      if (unmounted.current) return
      const delay = nextRetryDelay(retryAttempt.current++)
      retryTimer.current = setTimeout(() => connectRef.current(), delay)
    }
  }

  useEffect(() => {
    unmounted.current = false
    connectRef.current()
    return () => {
      unmounted.current = true
      if (retryTimer.current) clearTimeout(retryTimer.current)
      esRef.current?.close()
      esRef.current = null
    }
  }, [])

  const markRead = useCallback(async (id: string) => {
    // Optimistic
    setSseNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    setDbNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    setDbUnreadCount(prev => Math.max(0, prev - 1))
    try {
      await fetch('/api/v1/notifications/ack', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      })
    } catch {
      // non-fatal
    }
  }, [])

  const markAllRead = useCallback(async () => {
    setSseNotifications(prev => prev.map(n => ({ ...n, read: true })))
    setDbNotifications(prev => prev.map(n => ({ ...n, read: true })))
    setDbUnreadCount(0)
    try {
      await fetch('/api/v1/notifications/mark-all-read', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
    } catch {
      // non-fatal
    }
  }, [])

  const dismissOne = useCallback(async (id: string) => {
    setSseNotifications(prev => prev.filter(n => n.id !== id))
    setDbNotifications(prev => prev.filter(n => n.id !== id))
    setDbUnreadCount(prev => {
      const wasUnread = [...sseNotifications, ...dbNotifications].find(n => n.id === id && !n.read)
      return wasUnread ? Math.max(0, prev - 1) : prev
    })
    try {
      await fetch(`/api/v1/notifications/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
    } catch {
      // non-fatal
    }
  }, [sseNotifications, dbNotifications])

  const clearAll = useCallback(async () => {
    setSseNotifications([])
    setDbNotifications([])
    setDbUnreadCount(0)
    try {
      await fetch('/api/v1/notifications/clear-all', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
    } catch {
      // non-fatal
    }
  }, [])

  // FCM web push — register token with elmessagehub via the new /register-device endpoint.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      const token = await requestPermissionAndGetToken()
      if (!token || cancelled) return
      try {
        await fetch('/api/v1/notifications/register-device', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
      } catch {
        // best-effort — SSE still works
      }
    })()
    return () => { cancelled = true }
  }, [])

  // FCM foreground messages — chime when push arrives while tab is focused.
  useEffect(() => {
    const unsub = onForegroundMessage(() => {
      playNotificationChime()
      void fetchStored()
    })
    return unsub
  }, [fetchStored])

  return { notifications, unreadCount, markRead, markAllRead, clearAll, dismissOne, loading }
}
